"use client";

const DEFAULT_ERROR_HANDLER = (message: string) => {
  /**@todo replace with more UX-friendly error handling */
  alert(message);
};
const GOOGLE_MAPS_API_ENDPOINT =
  "https://maps.googleapis.com/maps/api/geocode/json";

type GetPostalCodeFromCurrentLocationParams = {
  /**
   * The function to call when an error occurs.
   * @default native alert()
   */
  onError?: (message: string) => void;
};

/**
 * Gets the postal code from the current location using the Google Maps API.
 * @returns The postal code or null if an error occurs.
 */
async function getPostalCodeFromCurrentLocation({
  onError = DEFAULT_ERROR_HANDLER,
}: GetPostalCodeFromCurrentLocationParams = {}): Promise<string | null> {
  if (!navigator.geolocation) {
    onError("Geolocation is not supported by your browser.");
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `${GOOGLE_MAPS_API_ENDPOINT}?latlng=${latitude},${longitude}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`,
          );
          const data = await response.json();

          if (data.status !== "OK" || !data.results.length) {
            onError("Could not determine address from your location.");
            resolve(null);
            return;
          }

          const postalCode = data.results[0].address_components.find(
            (comp: { types: string[] }) => comp.types.includes("postal_code"),
          )?.long_name;

          resolve(postalCode || null);
        } catch (error) {
          console.error("Reverse geocode failed", error);
          onError("Error fetching address for your location.");
          resolve(null);
        }
      },
      () => {
        onError("Unable to retrieve your location.");
        resolve(null);
      },
    );
  });
}

export { getPostalCodeFromCurrentLocation };
